// ** React Imports
import { ElementType } from 'react'

// ** Next Imports
import Link from 'next/link'
import { useRouter } from 'next/router'

// ** MUI Imports
import Chip from '@mui/material/Chip'
import ListItem from '@mui/material/ListItem'
import { styled } from '@mui/material/styles'
import Box, { BoxProps } from '@mui/material/Box'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemButton, { ListItemButtonProps } from '@mui/material/ListItemButton'
import Tooltip from '@mui/material/Tooltip'

// ** Configs Import
import themeConfig from 'src/configs/themeConfig'

// ** Types
import { NavLink, NavGroup } from 'src/@core/layouts/types'
import { Settings } from 'src/@core/context/settingsContext'

// ** Custom Components Imports
import UserIcon from 'src/layouts/components/UserIcon'
import Translations from 'src/layouts/components/Translations'
import CanViewNavLink from 'src/layouts/components/acl/CanViewNavLink'

// ** Util Imports
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { resolveNavPath } from 'src/@core/layouts/utils'
import { NavTruncationTooltip } from '@/src/layouts/bhyve-navigation-v2/NavTruncationTooltip'
import { isPrimaryNavLinkActive } from '@/src/layouts/bhyve-navigation-v2/navLinkActive'

interface Props {
  parent?: boolean
  item: NavLink
  navHover?: boolean
  settings: Settings
  navVisible?: boolean
  collapsedNavWidth: number
  navigationBorderWidth: number
  toggleNavVisibility: () => void
  isSubToSub?: NavGroup | undefined
}

// ** Styled Components
const MenuNavLink = styled(ListItemButton)<
  ListItemButtonProps & { component?: ElementType; href: string; target?: '_blank' | undefined }
>(({ theme }) => ({
  width: '100%',
  marginLeft: theme.spacing(3.5),
  marginRight: theme.spacing(3.5),
  borderRadius: theme.shape.borderRadius,
  transition: 'padding-left .25s ease-in-out, padding-right .25s ease-in-out',
  '&:hover': {
    backgroundColor: `${theme.palette.grey[800]}`,
    '& .MuiTypography-root': {
      color: `${theme.palette.common.white}`
    },
    '& svg': {
      color: `${theme.palette.common.white}`
    }
  },
  '& svg': {
    color: `${theme.palette.common.white}`
  },
  '&.active': {
    background: '#ffd204',
    '&:hover': {
      background: `${
        theme.direction === 'ltr' ? theme.palette.secondary.main : hexToRGBA(theme.palette.secondary.main, 0.7)
      }`,
      '&.Mui-focusVisible': {
        background: `${
          theme.direction === 'ltr' ? theme.palette.secondary.main : hexToRGBA(theme.palette.secondary.main, 0.7)
        }`
      },
      '& .MuiTypography-root': {
        color: `${theme.palette.common.white}`
      },
      '& svg': {
        color: `${theme.palette.common.white}`
      }
    },
    '& .MuiTypography-root': {
      color: `${theme.palette.text.primary}`
    },
    '& svg': {
      color: `${theme.palette.text.primary}`
    }
  }
}))

const MenuItemTextMetaWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.menuTextTruncate && { overflow: 'hidden' })
}))

const VerticalNavLink = ({
  item,
  parent,
  navHover,
  settings,
  navVisible,
  isSubToSub,
  collapsedNavWidth,
  toggleNavVisibility,
  navigationBorderWidth
}: Props) => {
  // ** Hooks
  const router = useRouter()
  const { id } = router.query

  // ** Vars

  const { navCollapsed } = settings

  const icon = parent && !item.icon ? themeConfig.navSubItemIcon : item.icon

  // Resolve navigation path, replacing [id] placeholder if valid
  const path: string = resolveNavPath(item.path, id as string | undefined)

  const isNavLinkActive = () => {
    if (!path) return false

    return isPrimaryNavLinkActive(router, path)
  }

  return (
    <CanViewNavLink navLink={item}>
      <ListItem
        disablePadding
        className='nav-link'
        disabled={item.disabled || false}
        sx={{ mt: 1, px: '0 !important' }}
      >
        <Tooltip
          title={item.title}
          placement='right'
          enterDelay={400}
          disableHoverListener={!(navCollapsed && !navHover)}
        >
          <MenuNavLink
            component={Link}
            {...(item.disabled && { tabIndex: -1 })}
            className={isNavLinkActive() ? 'active' : ''}
            href={path}
            {...(item.openInNewTab ? { target: '_blank' } : null)}
            onClick={e => {
              if (item.path === undefined) {
                e.preventDefault()
                e.stopPropagation()
              }
              if (navVisible) {
                toggleNavVisibility()
              }
            }}
            sx={{
              py: 2,
              ...(item.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
              px: navCollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 22 - 28) / 8 : 4,
              '& .MuiTypography-root, & svg': {
                color: 'common.white'
              }
            }}
          >
            <ListItemIcon
              sx={{
                transition: 'margin .25s ease-in-out',
                ...(navCollapsed && !navHover ? { mr: 0 } : { mr: 2 }),
                ...(parent ? { ml: 1.5, mr: 3.5 } : {}), // This line should be after (navCollapsed && !navHover) condition for proper styling
                '& svg': {
                  fontSize: '1.375rem',
                  ...(!parent ? { fontSize: '1.375rem' } : {}),
                  ...(parent && item.icon ? { fontSize: '1.375rem' } : {})
                }
              }}
            >
              {!parent && <UserIcon icon={icon as string} />}
            </ListItemIcon>

            <MenuItemTextMetaWrapper
              sx={{
                ...(isSubToSub ? { ml: 2 } : {})
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <NavTruncationTooltip title={item.title} variant='body1' placement='right' sx={{ color: 'inherit' }}>
                  <Translations text={item.title} />
                </NavTruncationTooltip>
              </Box>
              {item.badgeContent ? (
                <Chip
                  size='small'
                  label={item.badgeContent}
                  color={item.badgeColor || 'primary'}
                  sx={{ height: 22, minWidth: 22, '& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' } }}
                />
              ) : null}
            </MenuItemTextMetaWrapper>
          </MenuNavLink>
        </Tooltip>
      </ListItem>
    </CanViewNavLink>
  )
}

export default VerticalNavLink
